import React, { useCallback, useEffect } from 'react'
import {
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Box,
  useTheme,
  IconButton,
} from '@mui/material'

import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
} from 'recharts'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import {
  API_VERSION_V3,
  DATE_DIRECTIONS,
  getStartAndEndDate,
  GRAPH_PERIODS,
  VITAL_KEYS,
} from '../Constant'
import dayjs from 'dayjs'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { getVitalTracking } from '../../../../../services/DeviceServices'
import { useAppSelector } from '../../../../../app/hooks'
import { selectPatientData } from '../../../OpdPatientSlice'

const styles = (theme: any) => ({
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },

  toggleBtn: {
    fontSize: '14px',
    fontWeight: 600,
    minWidth: '60px',
  },
  dateDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },

  card: {
    backgroundColor: '#2c2c2c', // dark background
    color: '#fff',
    padding: theme.spacing(1),
    borderRadius: 12,
    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
    minWidth: 200,
  },
  title: {
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
  greenDot: {
    backgroundColor: 'green',
  },
  redDot: {
    backgroundColor: 'red',
  },
})

const ActivityTracking: React.FC = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const [period, setPeriod] = React.useState(GRAPH_PERIODS.DAY)
  const [currentDate, setCurrentDate] = React.useState(dayjs())
  const [chartData, setChartData] = React.useState([])
  const [avgData, setAvgData] = React.useState<any>({})

  const { startDate, endDate } = React.useMemo(() => {
    return getStartAndEndDate(currentDate, period)
  }, [currentDate, period])

  const patient = useAppSelector(selectPatientData)

  const handlePeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newPeriod: string | null
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod)
    }
  }

  const changeDate = (direction: string) => {
    const amount =
      period === GRAPH_PERIODS?.DAY
        ? 1
        : period === GRAPH_PERIODS?.WEEK
        ? 7
        : 30
    setCurrentDate((prev) =>
      prev.add(direction === DATE_DIRECTIONS?.NEXT ? amount : -amount, 'day')
    )
  }

  const handleGetActivity = useCallback(async () => {
    const response = await getVitalTracking(
      patient?.muhId,
      VITAL_KEYS?.ACTIVITY,
      API_VERSION_V3,
      startDate.format('YYYY-MM-DD'),
      endDate.format('YYYY-MM-DD'),
      period.toUpperCase()
    )
    if (response?.success) {
      setChartData(
        period === GRAPH_PERIODS?.DAY
          ? response?.data?.hourlyStepCounts
          : response?.data?.dailyData
      )
      setAvgData({
        steps: response?.data?.totalActivity?.calculatedStepCount,
        calories: response?.data?.totalActivity?.calories,
        distance: response?.data?.totalActivity?.distance,
        totalSteps: response?.data?.totalSteps,
        averageSteps: response?.data?.averageSteps,
      })
    }
  }, [patient?.muhId, startDate, endDate, period])

  useEffect(() => {
    handleGetActivity()
  }, [handleGetActivity])

  return (
    <Box m={3}>
      <Box className={classes?.toggleGroup}>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          color='primary'
          size='small'
        >
          <ToggleButton value='day' className={classes?.toggleBtn}>
            D
          </ToggleButton>
          <ToggleButton value='week' className={classes?.toggleBtn}>
            W
          </ToggleButton>
          <ToggleButton value='month' className={classes?.toggleBtn}>
            M
          </ToggleButton>
        </ToggleButtonGroup>
        <Box className={classes?.dateDisplay}>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.PREVIOUS)}
            color='inherit'
            aria-label='chevron left'
          >
            <ChevronLeft />
          </IconButton>
          <Typography>
            {currentDate.format(
              period === GRAPH_PERIODS?.DAY
                ? 'MMM DD, YYYY'
                : period === GRAPH_PERIODS?.WEEK
                ? '[Week of] MMM DD, YYYY'
                : 'MMM YYYY'
            )}
          </Typography>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.NEXT)}
            color='inherit'
            aria-label='chevron right'
            disabled={
              currentDate.isSame(dayjs(), 'day') ||
              currentDate.isAfter(dayjs(), 'day')
            }
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2} direction={'column'}>
        <ResponsiveContainer width='100%' height={300}>
          <BarChart data={chartData} margin={{ bottom: 40, top: 40 }}>
            <XAxis
              dataKey={period === GRAPH_PERIODS?.DAY ? 'time' : 'date'}
              fontSize={'12px'}
              interval={0}
              angle={-90}
              textAnchor='end'
              tick={{ fontSize: 12 }}
            />
            <CartesianGrid strokeDasharray='3 3' />
            <YAxis domain={[0, 'dataMax']} fontSize='12px' />
            <Tooltip />
            <Bar
              dataKey={
                period === GRAPH_PERIODS?.DAY ? 'stepCount' : 'totalSteps'
              }
              strokeWidth={1.5}
              fill='red'
              barSize={6}
              radius={[4, 4, 0, 0]}
            >
              {chartData?.map((entry: any, index) => (
                <Cell key={`cell-${index}`} fill='#4682b4' />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={3}
          mt={1.5}
          ml={4}
        >
          {period === GRAPH_PERIODS?.DAY ? (
            <>
              <Grid
                item
                xs={6}
                textAlign={'center'}
                spacing={2}
                border={'1px solid grey'}
                borderRadius={'20px'}
                p={2}
              >
                <Typography variant='h6'>{avgData?.steps}</Typography>
                <Typography variant='subtitle2' fontWeight={600}>
                  Steps
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                textAlign={'center'}
                spacing={2}
                border={'1px solid grey'}
                borderRadius={'20px'}
                p={2}
              >
                <Typography variant='h6'>{avgData?.distance} km</Typography>
                <Typography variant='subtitle2' fontWeight={600}>
                  Distance
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                textAlign={'center'}
                spacing={2}
                border={'1px solid grey'}
                borderRadius={'20px'}
                p={2}
              >
                <Typography variant='h6'>{avgData?.calories}</Typography>
                <Typography variant='subtitle2' fontWeight={600}>
                  Calories
                </Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid
                item
                xs={6}
                textAlign={'center'}
                spacing={2}
                border={'1px solid grey'}
                borderRadius={'20px'}
                p={2}
              >
                <Typography variant='h6'>{avgData?.totalSteps}</Typography>
                <Typography variant='subtitle2' fontWeight={600}>
                  Total Steps
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                textAlign={'center'}
                spacing={2}
                border={'1px solid grey'}
                borderRadius={'20px'}
                p={2}
              >
                <Typography variant='h6'>{avgData?.averageSteps}</Typography>
                <Typography variant='subtitle2' fontWeight={600}>
                  Average Steps
                </Typography>
              </Grid>
            </>
          )}
        </Box>
      </Grid>
    </Box>
  )
}

export default ActivityTracking
